import React, { useState, useMemo, useRef, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import {
  ChatContainer,
  ConversationHeader,
  MessageList,
  Message,
  MessageInput,
  MessageSeparator,
  Avatar,
} from '@chatscope/chat-ui-kit-react';
import groupBy from 'lodash/groupBy';
import noop from 'lodash/noop';
import random from 'lodash/random';
import { useTranslation } from 'react-i18next';

import { DateTime } from 'luxon';
import { timeOfDay, timestampShort } from '../helpers/timestamps';
import { renderMarkdown } from '../helpers/markdown';
import icon from '../images/ai-percy.png';
import ChatButtons from './ChatButtons';
import ThinkingIndicator from './ThinkingIndicator';
import InactivityRatingPrompt from './InactivityRatingPrompt';

function MessageContent(props) {

  // message content that renders markdown formatting
  // and animates typing

  const text = props.text ?? '';
  const fast = props.fast ?? false;
  const animateTyping = props.animateTyping ?? false;
  const onAnimate = props.onAnimate ?? noop;

  const [ end, setEnd ] = useState(animateTyping ? 3 : text.length);

  useEffect(() => {
    const t = setTimeout(() => {

      if (end >= text.length) return;

      const r = random(1, fast ? 30 : 15);
      setEnd((prev) => prev + r);

      // on every animation "tick", call this function...
      // we're using it to make the list scroll to the bottom 
      // so that the newly typed text remains visible
      onAnimate();

    }, fast ? 50 : 75);

    return () => clearTimeout(t);
  }, [ end, text ]);

  const typed = text.substring(0, end);
  const html = renderMarkdown(typed);

  return (
    <div dangerouslySetInnerHTML={{ __html: html }} className="chat-prose">
    </div>
  );
}

function CustomMessage({ message, scrollToBottom }) {

  const [ showTime, setShowTime ] = useState(false);

  const isToday = DateTime.now().toLocal().hasSame(
    DateTime.fromISO(message.timestamp).toLocal(), 
    'day'
  );
  const ts = isToday ?
    timeOfDay(message.timestamp) :
    timestampShort(message.timestamp) + ' ' + timeOfDay(message.timestamp);

  return (
    <Message
      model={{
        sender: message.author,
        sentTime: message.timestamp,
        direction: message.isMe ? 'outgoing' : 'incoming',
        position: 'single',
        type: 'custom',
      }}>
      { message.isMe && <Message.Header sender={message.author} /> }
      { showTime &&
        <Message.Footer sentTime={ts} />
      }
      <Message.CustomContent>
        <div role="button" 
          onClick={() => setShowTime(!showTime)}
        >
          <MessageContent 
            text={message.content} 
            animateTyping={message.animateTyping} 
            onAnimate={scrollToBottom}
          />
        </div>
      </Message.CustomContent>
    </Message>
  );
}

export default function AIAssistantChat(props) {
  const { t } = useTranslation();
  const {
    messages = [],
    onClose = noop,
    onSend = noop,
    isTyping,
    streamingText,
    isLoadingMore,
    loadMore = noop,
    showRatingPrompt = false,
    userEmail = '',
  } = props;

  const [ showChatButtons, setShowChatButtons ] = useState(true);

  const messagesByDate = useMemo(() => {
    return groupBy(messages, (message) => {
      return DateTime.fromISO(message.timestamp).toLocal().toFormat('yyyy-MM-dd');
    });
  }, [ messages ]);

  const dates = useMemo(() => {
    return Object.keys(messagesByDate).sort((d1, d2) => {
      return DateTime.fromFormat(d1, 'yyyy-MM-dd').toMillis() - 
        DateTime.fromFormat(d2, 'yyyy-MM-dd').toMillis();
    });
  }, [ messagesByDate ]);

  const listRef = useRef();

  // because the messages are animated, the size keeps changing, so 
  // we have to keep calling this function to make it scroll to the bottom
  function scrollToBottom() {
    if (listRef.current) {
      listRef.current.scrollToBottom();
    }
  }

  function scrollAndSend(message) {
    scrollToBottom();
    onSend(message);
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
    <ChatContainer className="chat-container" style={{flex: 1, minHeight: 0}}>
      <ConversationHeader className="conversation-header">
        <Avatar src={icon} className="avatar" />
        <ConversationHeader.Content>
          <div className="d-flex flex-column align-items-center">
            <div className="title">
              {t('heroes.ai-assistant.ai-assistant')}&nbsp;<span style={{
                background: '#f0ddd8',
                color: '#c47a6a',
                fontSize: '0.6em',
                fontWeight: 500,
                padding: '2px 8px',
                borderRadius: '10px',
                verticalAlign: 'middle',
              }}>beta</span>
            </div>
            <div className="subtitle badge badge-pill">
              <span style={{filter: 'brightness(10)'}}>&#x1F43E;</span>&nbsp;{t('heroes.ai-assistant.subtitle')}
            </div>
          </div>
        </ConversationHeader.Content>

        <ConversationHeader.Actions>
          <Button variant="ghost" onClick={onClose} className="conversation-header-close-button">
            &times;
          </Button>
        </ConversationHeader.Actions>
      </ConversationHeader>
      <MessageList 
        className="chat-message-list"
        ref={listRef}
        typingIndicator={null}
        onYReachStart={loadMore}
        loadingMore={isLoadingMore}
      >
        <>
          {dates.map((date) => (
            <React.Fragment key={date}>
              <MessageSeparator content={timestampShort(date)} />
              {messagesByDate[date].map((message) => (
                <CustomMessage 
                  key={message.id} 
                  message={message}
                  scrollToBottom={scrollToBottom}
                />
              ))}
            </React.Fragment>
          ))}
          { showChatButtons &&
            <MessageList.Content className="mt-4">
              <ChatButtons
                sendMessage={scrollAndSend}
                onHide={() => setShowChatButtons(false)}
              />
            </MessageList.Content>
          }
          { isTyping && (
            streamingText ? (
              <Message
                model={{
                  sender: t('heroes.ai-assistant.ai-assistant'),
                  direction: 'incoming',
                  position: 'single',
                  type: 'custom',
                }}>
                <Message.CustomContent>
                  <div className="chat-prose"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText) }}
                  />
                </Message.CustomContent>
              </Message>
            ) : (
              <MessageList.Content className="mt-2 mb-2">
                <ThinkingIndicator />
              </MessageList.Content>
            )
          )}
          { showRatingPrompt && !isTyping && (
            <MessageList.Content className="mt-2 mb-2">
              <InactivityRatingPrompt userEmail={userEmail} messages={messages} />
            </MessageList.Content>
          )}
        </>
      </MessageList>
      <MessageInput
        placeholder={t('heroes.ai-assistant.input-placeholder')}
        attachButton={false}
        onSend={(msg) => { setShowChatButtons(false); scrollAndSend(msg); }}
        autoFocus
      />

    </ChatContainer>
    <p className="percy-disclaimer">Percy can make mistakes. Check important info.</p>
    </div>
  );
}